import { ReactNode, useEffect, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore getSentry is in the parent dir
import { getSentry } from '@utils/sentry/getSentry';

import {
  DrumkitPlatform,
  SidebarState,
  SidebarStateContext,
  defaultSidebarState,
} from 'contexts/sidebarStateContext';

// Utility to set all SidebarState values as Sentry tags with dot notation
function setSentrySidebarStateTags(state: SidebarState) {
  const sentryScope = getSentry();
  Object.entries(state).forEach(([key, value]) => {
    // Only set primitive values (string, number, boolean)
    if (
      Boolean(value) &&
      (typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean')
    ) {
      sentryScope.setTag(`sidebarState.${key}`, String(value));
    }
  });
}

type SidebarBaseProps = {
  drumkitPlatform: DrumkitPlatform;
  children: ReactNode;
};

export type SidebarStateProviderProps = SidebarBaseProps & {
  inboxEmailAddress?: string; // Available in email client platforms (Gmail, Outlook, etc.)
  subject?: string; // Available in email client platforms (Gmail, Outlook, etc.); helpful for debugging
  // Outlook threading fields
  threadId?: string; // Available in email client platforms (Gmail, Outlook, etc.)
  threadItemId?: string; // Available in email client platforms (Gmail, Outlook, etc.)
  // Gmail threading fields (RFC standard)
  threadReferences?: string; // Available in Gmail/Front
  inReplyTo?: string[]; // Available in Gmail/Front
  isOutlookReply?: boolean; // Available in Outlook
  // True when item is in shared folder/mailbox (Office.js getSharedPropertiesAsync)
  // Note this is true only when user accesses shared mailbox via primary inbox; if they log in directly to
  // shared mailbox, this will be false (hence why code falls back to GetEmail()'s response.IsSharedMailboxUser)
  isSharedMailboxEmail?: boolean;
  isChromeSidePanel?: boolean; // Available only when running as Google Chrome extension
  // Available only in Chrome extension; note that this is the tab ID of the currently running sidepanel
  // and not the tab ID of the currently active tab.
  // Because the sidepanel runs independent of any webpage context, it's critical to use this to determine
  // if the sidepanel is the intended target of a chrome.runtime message.
  tabId?: number;
  // Thread participant information for email replies
  threadFrom?: string;
  threadTo?: string[];
  threadCc?: string[];
  openExternalUrl?: (url: string, target?: string, features?: string) => void;
};

export default function SidebarStateProvider({
  drumkitPlatform,
  children,
  inboxEmailAddress,
  subject,
  threadId,
  threadItemId,
  threadReferences,
  inReplyTo,
  isOutlookReply,
  isSharedMailboxEmail,
  isChromeSidePanel,
  tabId,
  threadFrom,
  threadTo,
  threadCc,
  openExternalUrl,
}: SidebarStateProviderProps) {
  const [currentState, setCurrentState] = useState<SidebarState>({
    ...defaultSidebarState,
    tabId: tabId ?? 0,
    drumkitPlatform: drumkitPlatform,
    inboxEmailAddress: inboxEmailAddress ?? '',
    subject: subject ?? '',
    threadId: threadId ?? '',
    threadItemId: threadItemId ?? '',
    threadReferences: threadReferences ?? '',
    inReplyTo: inReplyTo ?? [],
    isOutlookReply: isOutlookReply ?? false,
    isSharedMailboxEmail: isSharedMailboxEmail ?? false,
    isChromeSidePanel: isChromeSidePanel ?? false,
    threadFrom: threadFrom ?? '',
    threadTo: threadTo ?? [],
    threadCc: threadCc ?? [],
    openExternalUrl: openExternalUrl ?? window.open,
  });

  // Set all sidebar state as Sentry tags
  useEffect(() => {
    setSentrySidebarStateTags(currentState);
  }, [currentState]);

  useEffect(() => {
    setCurrentState((prevState) => ({
      // Only reset state if thread ID has changed; prevents suggestions from being reset to empty
      ...(prevState.threadId === threadId ? prevState : defaultSidebarState),
      drumkitPlatform: drumkitPlatform,
      inboxEmailAddress: inboxEmailAddress ?? prevState.inboxEmailAddress,
      subject: subject ?? '',
      threadId: threadId ?? '',
      threadItemId: threadItemId ?? '',
      threadReferences: threadReferences ?? '',
      inReplyTo: inReplyTo ?? [],
      isOutlookReply: isOutlookReply ?? false,
      isSharedMailboxEmail: isSharedMailboxEmail ?? false,
      isChromeSidePanel: isChromeSidePanel ?? false,
      tabId: tabId ?? 0,
      threadFrom: threadFrom ?? '',
      threadTo: threadTo ?? [],
      threadCc: threadCc ?? [],
      // Preserve openExternalUrl from props (e.g. Front.openUrl) so external links work in embedded contexts
      openExternalUrl: openExternalUrl ?? prevState.openExternalUrl,
      // Preserve Kit's registered handler
      goToSuggestion: prevState.goToSuggestion,
    }));
  }, [
    drumkitPlatform,
    inboxEmailAddress,
    subject,
    threadId,
    threadItemId,
    threadReferences,
    inReplyTo,
    isOutlookReply,
    isSharedMailboxEmail,
    isChromeSidePanel,
    tabId,
    threadFrom,
    threadTo,
    threadCc,
    openExternalUrl,
  ]);

  return (
    <SidebarStateContext.Provider
      value={{
        currentState: currentState,
        setCurrentState: setCurrentState,
      }}
    >
      {children}
    </SidebarStateContext.Provider>
  );
}
